/**
 * <product-info>: product page behaviour. Picking option buttons selects the
 * matching variant and updates the price, stock, buttons, gallery image and
 * URL; thumbnails switch the gallery; -/+ step the quantity; Share uses the
 * Web Share API or copies the link; the purchase option dropdown is a
 * listbox that sets the hidden selling plan select. Without JS the page
 * still adds the preselected variant to the cart.
 * A custom element keeps working when the theme editor re-renders the section.
 */
class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.variants = this.readJson('[data-product-variants]') ?? [];
    this.strings = this.readJson('[data-product-strings]') ?? {};
    this.lowStockThreshold = Number(this.dataset.lowStockThreshold) || 0;

    const fallback = this.querySelector('[data-variant-fallback]');
    const fallbackSelect = this.querySelector('[data-variant-select]');
    const variantInput = this.querySelector('[data-variant-id]');
    if (fallback && fallbackSelect && variantInput) {
      fallback.hidden = true;
      fallbackSelect.disabled = true;
      variantInput.disabled = false;
    }

    this.currentVariant = this.variants.find((variant) => String(variant.id) === variantInput?.value);

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    this.syncPlanMenu();
    this.syncQuantityButtons();
    if (this.currentVariant) this.renderPrice(this.currentVariant);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    clearTimeout(this.shareTimer);
  }

  readJson(selector) {
    const script = this.querySelector(selector);
    try {
      return script ? JSON.parse(script.textContent) : null;
    } catch {
      return null;
    }
  }

  onChange = (event) => {
    if (event.target.matches('[data-selling-plan]')) {
      this.syncPlanMenu();
      if (this.currentVariant) this.renderPrice(this.currentVariant);
      this.syncPlanUrl();
      return;
    }
    const input = event.target.closest('[data-option-input]');
    if (!input) return;
    const selected = input.closest('[data-option]').querySelector('[data-option-selected]');
    if (selected) selected.textContent = input.value;
    if (input.dataset.productUrl || Number(this.dataset.variantCount) > 250) {
      const url = new URL(input.dataset.productUrl || window.location.href, window.location.origin);
      const values = [...this.querySelectorAll('[data-option-input]:checked')].map((option) => option.dataset.optionValueId);
      url.searchParams.delete('variant');
      if (!input.dataset.productUrl) url.searchParams.set('option_values', values.join(','));
      window.location.assign(url);
      return;
    }
    this.selectVariant();
  };

  onClick = (event) => {
    const planOption = event.target.closest('[data-plan-option]');
    if (planOption) {
      this.selectPlan(planOption);
      return;
    }
    const planTrigger = event.target.closest('[data-plan-trigger]');
    if (planTrigger) {
      this.togglePlanMenu(planTrigger.getAttribute('aria-expanded') !== 'true');
      return;
    }
    const thumb = event.target.closest('[data-thumb]');
    if (thumb) {
      this.showMedia(thumb.dataset.mediaId);
      return;
    }
    const step = event.target.closest('[data-qty-step]');
    if (step) {
      this.stepQuantity(Number(step.dataset.qtyStep));
      return;
    }
    const share = event.target.closest('[data-share]');
    if (share) this.share(share);
  };

  onInput = (event) => {
    if (event.target.matches('[data-quantity]')) this.syncQuantityButtons();
  };

  onKeydown = (event) => {
    if (event.target.closest('[data-plan-menu]')) this.onPlanKeydown(event);
  };

  onDocumentClick = (event) => {
    const menu = this.querySelector('[data-plan-menu][data-open]');
    if (menu && !menu.contains(event.target)) this.togglePlanMenu(false, { focus: false });
  };

  selectedOptions() {
    return [...this.querySelectorAll('[data-option]')].map(
      (fieldset) => fieldset.querySelector('[data-option-input]:checked')?.value
    );
  }

  selectVariant() {
    const options = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    this.markAvailability(options);
    this.render(variant);
  }

  /* Strike through values that have no available variant with the other current choices. */
  markAvailability(options) {
    this.querySelectorAll('[data-option]').forEach((fieldset, index) => {
      fieldset.querySelectorAll('[data-option-input]').forEach((input) => {
        const available = this.variants.some(
          (v) =>
            v.available &&
            v.options[index] === input.value &&
            v.options.every((value, i) => i === index || value === options[i])
        );
        input.closest('label')?.toggleAttribute('data-unavailable', !available);
      });
    });
  }

  render(variant) {
    const addButton = this.querySelector('[data-add-to-cart]');
    const addLabel = this.querySelector('[data-add-label]');
    const buyButton = this.querySelector('[data-buy-now]');
    const status = this.querySelector('[data-stock-status]');
    const low = this.querySelector('[data-stock-low]');

    if (!variant) {
      this.currentVariant = null;
      if (addButton) addButton.disabled = true;
      if (addLabel) addLabel.textContent = this.strings.unavailable;
      if (buyButton) buyButton.hidden = true;
      if (status) {
        status.textContent = this.strings.unavailable;
        status.classList.add('is-out');
      }
      if (low) low.hidden = true;
      const idInput = this.querySelector('[data-variant-id]');
      if (idInput) idInput.disabled = true;
      return;
    }

    this.currentVariant = variant;
    const idInput = this.querySelector('[data-variant-id]');
    if (idInput) {
      idInput.value = variant.id;
      idInput.disabled = false;
    }

    for (const key of ['sku', 'barcode']) {
      const row = this.querySelector(`[data-${key}-row]`);
      const value = this.querySelector(`[data-${key}]`);
      if (row) row.hidden = !variant[key];
      if (value) value.textContent = variant[key] || '';
    }

    const unit = this.querySelector('[data-unit-price]');
    if (unit) {
      unit.hidden = !variant.unitPrice;
      unit.querySelector('[data-unit-amount]').textContent = variant.unitPrice || '';
      unit.querySelector('[data-unit-reference]').textContent = variant.unitReference || '';
    }

    const quantity = this.querySelector('[data-quantity]');
    if (quantity) {
      quantity.min = variant.quantityMin;
      quantity.step = variant.quantityIncrement;
      if (variant.quantityMax !== null) quantity.max = variant.quantityMax;
      else quantity.removeAttribute('max');
      quantity.value = variant.quantityMin;
      this.syncQuantityButtons();
    }

    this.updateSellingPlans(variant);
    this.renderPrice(variant);
    if (idInput) idInput.dispatchEvent(new Event('change', { bubbles: true }));

    if (status) {
      status.textContent = variant.available ? this.strings.inStock : this.strings.outOfStock;
      status.classList.toggle('is-out', !variant.available);
    }
    if (low) {
      const showLow = variant.stock !== null && variant.stock > 0 && variant.stock <= this.lowStockThreshold;
      low.textContent = showLow ? this.strings.lowStock.replace('[count]', variant.stock) : '';
      low.hidden = !showLow;
    }

    const purchasable = variant.available && (!variant.requiresPlan || variant.plans.length > 0);
    if (addButton) addButton.disabled = !purchasable;
    if (addLabel) addLabel.textContent = purchasable ? this.strings.addToCart : this.strings.soldOut;
    if (buyButton) buyButton.hidden = !purchasable;

    if (variant.media) this.showMedia(String(variant.media));

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    url.searchParams.delete('option_values');
    const plan = this.querySelector('[data-selling-plan]');
    if (plan && !plan.disabled && plan.value) url.searchParams.set('selling_plan', plan.value);
    else url.searchParams.delete('selling_plan');
    window.history.replaceState(window.history.state, '', url);
  }

  syncPlanUrl() {
    const select = this.querySelector('[data-selling-plan]');
    const url = new URL(window.location.href);
    if (select && !select.disabled && select.value) url.searchParams.set('selling_plan', select.value);
    else url.searchParams.delete('selling_plan');
    window.history.replaceState(window.history.state, '', url);
  }

  updateSellingPlans(variant) {
    const select = this.querySelector('[data-selling-plan]');
    const wrapper = this.querySelector('[data-plan-wrapper]');
    if (!select || !wrapper) return;
    const selected = select.value;
    select.replaceChildren();
    if (!variant.requiresPlan) select.add(new Option(this.strings.oneTime, ''));
    for (const plan of variant.plans) select.add(new Option(`${plan.name} — ${plan.price}`, String(plan.id)));
    if ([...select.options].some((option) => option.value === selected)) select.value = selected;
    select.required = variant.requiresPlan;
    select.disabled = variant.plans.length === 0 && !variant.requiresPlan;
    wrapper.hidden = select.disabled;
    this.syncPlanMenu();
  }

  /* Purchase option dropdown: the same sort-menu listbox as the collection page, mirroring the select. */
  get planMenu() {
    return this.querySelector('[data-plan-menu]');
  }

  planOptions() {
    return [...(this.planMenu?.querySelectorAll('[data-plan-option]') ?? [])];
  }

  syncPlanMenu() {
    const menu = this.planMenu;
    const select = this.querySelector('[data-selling-plan]');
    const template = menu?.querySelector('[data-plan-option-template]');
    if (!menu || !select || !template) return;
    const options = [...select.options].map((option) => {
      const item = template.content.firstElementChild.cloneNode(true);
      item.dataset.planOption = option.value;
      item.setAttribute('aria-selected', String(option.selected));
      item.querySelector('span').textContent = option.textContent.trim();
      return item;
    });
    menu.querySelector('[data-plan-list]').replaceChildren(...options);
    menu.querySelector('[data-plan-value]').textContent = select.selectedOptions[0]?.textContent.trim() ?? '';
  }

  togglePlanMenu(open, { focus = true } = {}) {
    const menu = this.planMenu;
    if (!menu) return;
    const trigger = menu.querySelector('[data-plan-trigger]');
    menu.toggleAttribute('data-open', open);
    trigger.setAttribute('aria-expanded', String(open));
    if (!focus) return;
    if (open) {
      const options = this.planOptions();
      (options.find((option) => option.getAttribute('aria-selected') === 'true') ?? options[0])?.focus();
    } else {
      trigger.focus();
    }
  }

  selectPlan(option) {
    const select = this.querySelector('[data-selling-plan]');
    this.togglePlanMenu(false);
    if (select && select.value !== option.dataset.planOption) {
      select.value = option.dataset.planOption;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onPlanKeydown(event) {
    const open = this.planMenu.hasAttribute('data-open');
    const options = this.planOptions();
    const index = options.indexOf(document.activeElement);

    if (!open) {
      if (event.target.closest('[data-plan-trigger]') && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.togglePlanMenu(true);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.togglePlanMenu(false);
        break;
      case 'Tab':
        this.togglePlanMenu(false, { focus: false });
        break;
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = index === -1 ? 0 : (index + step + options.length) % options.length;
        options[next]?.focus();
        break;
      }
      case 'Home':
      case 'End':
        event.preventDefault();
        options[event.key === 'Home' ? 0 : options.length - 1]?.focus();
        break;
    }
  }

  renderPrice(variant) {
    const price = this.querySelector('[data-price]');
    const compare = this.querySelector('[data-compare]');
    const saving = this.querySelector('[data-saving]');
    const priceLabel = this.querySelector('[data-price-label]');
    const selectedPlan = this.querySelector('[data-selling-plan]')?.value;
    const plan = variant.plans.find((item) => String(item.id) === selectedPlan);
    const onSale = Boolean(variant.compare) && !plan;
    if (priceLabel) priceLabel.textContent = onSale ? this.strings.salePrice : this.strings.regularPrice;
    const unitAmount = this.querySelector('[data-unit-amount]');
    if (unitAmount) unitAmount.textContent = plan?.unitPrice || variant.unitPrice || '';
    if (price) {
      price.textContent = plan?.price || variant.price;
      price.classList.toggle('is-sale', onSale);
    }
    if (compare) {
      compare.textContent = variant.compare ?? '';
      compare.hidden = !onSale;
    }
    if (saving) {
      saving.textContent = onSale ? this.strings.saveAmount.replace('[amount]', variant.saving) : '';
      saving.hidden = !onSale;
    }

  }

  showMedia(id) {
    this.querySelectorAll('.product-gallery__slide[data-media-id]').forEach((slide) => {
      const active = slide.dataset.mediaId === id;
      if (!active) slide.querySelector('video')?.pause();
      slide.hidden = !active;
    });
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      if (thumb.dataset.mediaId === id) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  stepQuantity(step) {
    const input = this.querySelector('[data-quantity]');
    if (!input) return;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const increment = Number(input.step) || 1;
    input.value = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + step * increment));
    this.syncQuantityButtons();
  }

  syncQuantityButtons() {
    const input = this.querySelector('[data-quantity]');
    const minus = this.querySelector('[data-qty-step="-1"]');
    const plus = this.querySelector('[data-qty-step="1"]');
    if (input && minus) minus.disabled = (parseInt(input.value, 10) || Number(input.min)) <= Number(input.min);
    if (input && plus) plus.disabled = Boolean(input.max) && Number(input.value) >= Number(input.max);
  }

  async share(button) {
    const url = button.dataset.shareUrl || window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: document.title, url });
      } catch {
        /* Dismissed by the visitor. */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return;
    }
    const label = button.querySelector('[data-share-label]');
    if (!label) return;
    label.dataset.original ??= label.textContent;
    label.textContent = this.strings.linkCopied;
    clearTimeout(this.shareTimer);
    this.shareTimer = setTimeout(() => {
      label.textContent = label.dataset.original;
    }, 2000);
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
